/**
 * Rooms service
 * Live group chat: the rooms you are in, the ones you can ask to join, and
 * everything inside one.
 */

import api from "../api";

// ─── Types ────────────────────────────────────────────────────────────────────

export type MemberStatus = "requested" | "active" | "declined" | "removed" | "left";
export type MemberRole = "admin" | "member";

export interface RoomMessage {
  id: string;
  seq: number;
  room: string;
  kind: "text" | "system";
  body: string;
  /** Echoed back so an optimistic bubble can be matched to its saved message. */
  client_msg_id: string | null;
  reply_to: {
    id: string;
    seq: number;
    author: string;
    body: string;
    is_gone: boolean;
  } | null;
  is_deleted: boolean;
  is_hidden: boolean;
  /** You blocked the sender: the message keeps its place and loses its text. */
  is_blocked?: boolean;
  /** Pictures. Empty once they expire, and for a blocked sender. */
  media?: { id: string; kind: string; url: string | null; width?: number; height?: number }[];
  created_at: string;
  author: string;
  author_handle_id: string | null;
  author_is_verified: boolean;
  author_profile_picture: string | null;
  /** Decided on the server, so the client never needs an account id. */
  is_mine?: boolean;
}

export type CannotRequestReason = "full" | "archived" | "requested" | null;

export interface RoomSummary {
  id: string;
  name: string;
  description: string;
  member_count: number;
  member_limit: number;
  is_archived: boolean;
  /** When the room started. Visible to members and strangers alike. */
  created_at?: string;
  /** Below five members a "seen by" count would name a person. */
  hides_counts: boolean;
  my_status: MemberStatus | null;
  my_role: MemberRole | null;
  is_member: boolean;
  /** Members only. */
  unread_count?: number;
  last_seq?: number;
  last_read_seq?: number;
  last_message?: {
    seq: number;
    author: string;
    body: string;
    /** A picture on its own is a message, so the body can be empty with something
        to see. The server has to carry this: the preview has no handle id and no
        media of its own, so no client can work it out. */
    has_media?: boolean;
    created_at: string;
  } | null;
  /** Admins only. */
  pending_requests?: number;
  /** Non-members only. */
  can_request?: boolean;
  /** Why the door is shut, when it is safe to say. Never "declined". */
  cannot_request_reason?: CannotRequestReason;
}

export interface RoomList {
  rooms: RoomSummary[];
  discover: RoomSummary[];
}

export interface RoomMembers {
  members: { id: string; handle: string; role: MemberRole; is_me: boolean }[];
  my_role: MemberRole;
  /** Admins only: who is waiting at the door. */
  requests?: { id: string; handle: string; requested_at: string }[];
}

export interface RoomInvite {
  id: string;
  token: string;
  expires_at: string;
  revoked: boolean;
  is_usable: boolean;
  uses: number;
}

export interface MessagePage {
  results: RoomMessage[];
  last_seq: number;
  has_more: boolean;
}

export interface ConnectionToken {
  token: string;
  /** The member's own channel: everything they can hear arrives on it. */
  channel: string;
  expires_in: number;
}

// ─── The live connection ──────────────────────────────────────────────────────

export async function getConnectionToken(): Promise<ConnectionToken> {
  const res = await api.post<ConnectionToken>("/api/rooms/connection-token/");
  return res.data;
}

// ─── Rooms ────────────────────────────────────────────────────────────────────

export async function getRooms(): Promise<RoomList> {
  const res = await api.get<RoomList>("/api/rooms/");
  return res.data;
}

export async function getRoom(roomId: string): Promise<RoomSummary> {
  const res = await api.get<RoomSummary>(`/api/rooms/${roomId}/`);
  return res.data;
}

export async function updateRoom(
  roomId: string,
  changes: { name?: string; description?: string },
): Promise<RoomSummary> {
  const res = await api.patch<RoomSummary>(`/api/rooms/${roomId}/`, changes);
  return res.data;
}

export async function requestToJoin(roomId: string): Promise<{ status: MemberStatus }> {
  const res = await api.post<{ status: MemberStatus }>(`/api/rooms/${roomId}/join-request/`);
  return res.data;
}

export async function leaveRoom(roomId: string): Promise<void> {
  await api.post(`/api/rooms/${roomId}/leave/`);
}

// ─── Messages ─────────────────────────────────────────────────────────────────

export async function getMessages(
  roomId: string,
  params?: { after_seq?: number; before_seq?: number },
): Promise<MessagePage> {
  const res = await api.get<MessagePage>(`/api/rooms/${roomId}/messages/`, { params });
  return res.data;
}

export async function sendMessage(
  roomId: string,
  payload: { body: string; client_msg_id: string; reply_to?: string; media_ids?: string[] },
): Promise<RoomMessage> {
  const res = await api.post<RoomMessage>(`/api/rooms/${roomId}/messages/`, payload);
  return res.data;
}

export async function deleteMessage(roomId: string, messageId: string): Promise<void> {
  await api.delete(`/api/rooms/${roomId}/messages/${messageId}/`);
}

export async function hideMessage(roomId: string, messageId: string): Promise<void> {
  await api.post(`/api/rooms/${roomId}/messages/${messageId}/hide/`);
}

export async function markRead(
  roomId: string,
  seq: number,
): Promise<{ last_read_seq: number; unread_count: number }> {
  const res = await api.post(`/api/rooms/${roomId}/read/`, { seq });
  return res.data;
}

// ─── Members, requests and invites ────────────────────────────────────────────

export async function getMembers(roomId: string): Promise<RoomMembers> {
  const res = await api.get<RoomMembers>(`/api/rooms/${roomId}/members/`);
  return res.data;
}

export async function decideRequest(
  roomId: string,
  memberId: string,
  decision: "accept" | "decline",
): Promise<void> {
  await api.post(`/api/rooms/${roomId}/requests/${memberId}/${decision}/`);
}

export async function removeMember(roomId: string, memberId: string): Promise<void> {
  await api.delete(`/api/rooms/${roomId}/members/${memberId}/`);
}

export async function getInvites(roomId: string): Promise<{ invites: RoomInvite[] }> {
  const res = await api.get<{ invites: RoomInvite[] }>(`/api/rooms/${roomId}/invites/`);
  return res.data;
}

export async function createInvite(roomId: string): Promise<RoomInvite> {
  const res = await api.post<RoomInvite>(`/api/rooms/${roomId}/invites/`);
  return res.data;
}

export async function revokeInvite(roomId: string, inviteId: string): Promise<void> {
  await api.delete(`/api/rooms/${roomId}/invites/${inviteId}/`);
}

export async function acceptInvite(token: string): Promise<{ room: string; joined: boolean }> {
  const res = await api.post<{ room: string; joined: boolean }>(`/api/rooms/invites/${token}/accept/`);
  return res.data;
}

export interface InvitePreview {
  valid: boolean;
  room?: { name: string; description: string; member_count: number };
  expires_at?: string;
}

/** What a link is for, before signing in. Never anything from inside the room. */
export async function previewInvite(token: string): Promise<InvitePreview> {
  const res = await api.get<InvitePreview>(`/api/rooms/invites/${token}/`);
  return res.data;
}

// ─── Blocking ─────────────────────────────────────────────────────────────────

export interface BlockList {
  blocked: { username: string; handle: string; created_at: string }[];
  /** Handles to drop live messages from, before they are drawn. */
  handle_ids: string[];
}

export async function getBlocks(): Promise<BlockList> {
  const res = await api.get<BlockList>("/api/community/blocks/");
  return res.data;
}

export async function blockMember(username: string): Promise<void> {
  await api.post("/api/community/blocks/", { username });
}

export async function unblockMember(username: string): Promise<void> {
  await api.delete(`/api/community/blocks/${encodeURIComponent(username)}/`);
}

/** One number for the nav badge: everything unread across your rooms. */
export async function getUnreadCount(): Promise<{ count: number; rooms: number }> {
  const res = await api.get<{ count: number; rooms: number }>("/api/rooms/unread-count/");
  return res.data;
}
